/**
 * Shared utility functions for AI Resepsjonen portal.
 * Import from here to avoid duplication across pages.
 */

/**
 * Format duration in seconds to "Xm Ys" string.
 * @param {number} seconds
 * @returns {string} e.g. "2m 34s"
 */
export function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return '-';
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

/**
 * Format a Norwegian phone number.
 * @param {string|number} number
 * @returns {string} e.g. "+47 123 45 678"
 */
export function formatPhone(number) {
  if (!number) return '';
  const str = String(number).replace(/\s+/g, '');
  // Norwegian numbers: +47XXXXXXXX
  if (str.startsWith('+47') && str.length === 11) {
    const local = str.slice(3);
    return `+47 ${local.slice(0, 3)} ${local.slice(3, 5)} ${local.slice(5)}`;
  }
  // US numbers: +1XXXXXXXXXX
  if (str.startsWith('+1') && str.length === 12) {
    const local = str.slice(2);
    return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  return str;
}

/**
 * Format an ISO date string using Norwegian locale.
 * @param {string} isoString
 * @param {string} locale
 * @returns {string} e.g. "26. april 2026"
 */
export function formatDate(isoString, locale = 'nb-NO') {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return isoString;
  }
}

/**
 * Format a currency amount.
 * @param {number} amount
 * @param {string} currency
 * @returns {string} e.g. "1 990 kr"
 */
export function formatCurrency(amount, currency = 'NOK') {
  if (amount === null || amount === undefined) return '';
  try {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

/**
 * Truncate a string to a maximum length, adding ellipsis.
 * @param {string} str
 * @param {number} maxLen
 * @returns {string}
 */
export function truncate(str, maxLen = 50) {
  if (!str) return '';
  const s = String(str);
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen - 3) + '...';
}
